import './DeleteAccountModal.css'
import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material'
import { FaEye, FaEyeSlash, FaExclamationTriangle } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useToast } from 'context/ToastContext'
import { useAuth } from 'context/AuthContext'
import { deleteAccount } from 'services/authService'

interface Props {
  open: boolean
  onClose: () => void
}

const DeleteAccountModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const { logout, user } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const username = user?.username ?? ''

  const schema = yup.object({
    confirmUsername: yup
      .string()
      .required()
      .test('matches-username', t('settings.delete-account-modal.confirm-username-mismatch'),
        (val) => (val ?? '').trim() === username),
    password: yup.string().required(t('auth.password-required')),
  })

  const formik = useFormik({
    initialValues: { confirmUsername: '', password: '' },
    validationSchema: schema,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      try {
        const res = await deleteAccount(values.password)
        if (res.data.status === 1) {
          showToast('success', t('settings.delete-account-modal.success'))
          logout()
        }
      } catch (err: any) {
        const status = err?.response?.data?.status
        if (status === -4) {
          setFieldError('password', t('settings.delete-account-modal.error.wrong-password'))
        } else if (status === -3) {
          showToast('error', t('settings.delete-account-modal.error.already-scheduled'))
        } else {
          showToast('error', t('settings.delete-account-modal.error.bad-request'))
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleClose = () => {
    if (formik.isSubmitting) return
    formik.resetForm()
    setShowPassword(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        className="delete-account-modal-box"
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'error.main',
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" color="error">
          {t('settings.delete-account-modal.title')}
        </Typography>

        <Box
          className="delete-account-warning-block"
          sx={{ bgcolor: 'error.dark', opacity: 0.85 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaExclamationTriangle size={14} color='#fff' />
            <Typography variant="body2" fontWeight={600} sx={{ color: 'error.contrastText' }}>
              {t('settings.delete-account-modal.warning-title')}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: 'error.contrastText' }}
            dangerouslySetInnerHTML={{
              __html: t('settings.delete-account-modal.warning-body', { username }),
            }}
          />
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box className="delete-account-fields">
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.delete-account-modal.confirm-username-label')}
              placeholder={t('settings.delete-account-modal.confirm-username-placeholder', { username })}
              autoComplete="off"
              {...formik.getFieldProps('confirmUsername')}
              error={formik.touched.confirmUsername && Boolean(formik.errors.confirmUsername)}
              helperText={
                <span
                  dangerouslySetInnerHTML={{
                    __html: (formik.touched.confirmUsername && formik.errors.confirmUsername)
                      ? formik.errors.confirmUsername as string
                      : t('settings.delete-account-modal.confirm-username-helper', { username }),
                  }}
                />
              }
            />
            <TextField
              fullWidth
              variant="standard"
              label={t('settings.delete-account-modal.password-label')}
              placeholder={t('settings.delete-account-modal.password-placeholder')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...formik.getFieldProps('password')}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(p => !p)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            className="delete-account-footer-warning"
          >
            {t('settings.delete-account-modal.footer-warning')}
          </Typography>

          <Box className="delete-account-actions">
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={formik.isSubmitting}
            >
              {t('settings.delete-account-modal.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              type="submit"
              disabled={
                !formik.values.password
                || !formik.values.confirmUsername
                || formik.values.confirmUsername.trim() !== username
                || formik.isSubmitting
                || !formik.isValid
              }
              startIcon={
                formik.isSubmitting
                  ? <CircularProgress size={16} color="inherit" />
                  : null
              }
            >
              {t('settings.delete-account-modal.submit')}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}

export default DeleteAccountModal
